import { updateReviewBlockHighlights } from "./anchors";
import { fetchComments } from "./api";
import { state } from "./app-state";
import { renderCommentCard } from "./comments";
import { DOCS, resolveDocId } from "./constants";
import { renderIcons, ui } from "./dom";
import { filterComments } from "./state";

let documentCommentFilter: "open" | "all" = "open";

export function setDocumentCommentFilter(filter: "open" | "all"): void {
  documentCommentFilter = filter;
}

export function updateCommentCounts(): void {
  const openComments = state.allComments.filter((comment) => comment.status === "open");
  const openDocumentComments = state.comments.filter((comment) => comment.status === "open");
  ui.navOpenCount.textContent = String(openComments.length);
  ui.docCommentBadge.textContent = String(state.comments.length);
  ui.docCommentCount.textContent = String(state.comments.length);
  ui.docOpenCommentCount.textContent = String(openDocumentComments.length);
  ui.docAllCommentCount.textContent = String(state.comments.length);
  const boardLabel = `打开意见汇总，${openComments.length} 条待处理意见`;
  const documentLabel = `打开本文意见，${openDocumentComments.length} 条待处理意见`;
  ui.boardNavBtn.setAttribute("aria-label", boardLabel);
  ui.boardNavBtn.title = boardLabel;
  ui.commentsBtn.setAttribute("aria-label", documentLabel);
  ui.commentsBtn.title = documentLabel;

  DOCS.forEach((doc) => {
    const count = openComments.filter(
      (comment) => resolveDocId(comment.doc_id) === doc.id,
    ).length;
    const target = ui.docNav.querySelector<HTMLElement>(`[data-doc-open-count="${doc.id}"]`);
    if (target) {
      target.textContent = count ? String(count) : "";
      target.setAttribute("aria-label", count ? `${count} 条待处理意见` : "没有待处理意见");
      target.parentElement?.setAttribute("title", count ? `${count} 条待处理意见` : "没有待处理意见");
    }
  });
}

/** 评论卡片形骨架:首载/切换文档时占位,避免面板空白闪烁。 */
export function commentSkeletonHtml(count = 3): string {
  const card = `<div class="comment-skeleton" aria-hidden="true">
    <span class="comment-skeleton-line comment-skeleton-line--meta"></span>
    <span class="comment-skeleton-line"></span>
    <span class="comment-skeleton-line comment-skeleton-line--short"></span>
  </div>`;
  return `<div class="comment-skeleton-group" aria-label="正在加载意见">${card.repeat(count)}</div>`;
}

export function renderDocumentComments(): void {
  const comments = documentCommentFilter === "open"
    ? state.comments.filter((comment) => comment.status === "open")
    : state.comments;
  ui.docCommentList.innerHTML = comments.length
    ? comments.map((comment) => renderCommentCard(comment, {
        showDoc: false,
        isAdmin: state.session.isAdmin,
      })).join("")
    : `<div class="empty-state">
        <strong>${documentCommentFilter === "open" && state.comments.length ? "没有待处理意见" : "还没有意见"}</strong>
        <span>${documentCommentFilter === "open" && state.comments.length ? "可以切换到“全部”，查看已处理意见。" : "划选正文、评论段落，或提交一条全文意见。"}</span>
      </div>`;
  ui.docCommentFilters.querySelectorAll<HTMLButtonElement>("button[data-doc-comment-filter]").forEach((button) => {
    button.classList.toggle("active", button.dataset.docCommentFilter === documentCommentFilter);
  });
  updateCommentCounts();
  renderIcons();
}

export function renderBoard(): void {
  const comments = filterComments(state.allComments, state.filter, state.boardDocId);
  ui.boardCount.textContent = `${comments.length} 条`;
  ui.boardList.innerHTML = comments.length
    ? comments.map((comment) => renderCommentCard(comment, {
        showDoc: true,
        isAdmin: state.session.isAdmin,
      })).join("")
    : `<div class="empty-state">
        <strong>没有符合条件的意见</strong>
        <span>可以切换筛选条件，或提交一条项目总意见。</span>
      </div>`;
  renderIcons();
}

export async function refreshComments(options: { showSkeleton?: boolean } = {}): Promise<void> {
  if (options.showSkeleton) ui.docCommentList.innerHTML = commentSkeletonHtml();
  try {
    state.allComments = await fetchComments();
  } catch (error) {
    if (options.showSkeleton) ui.docCommentList.innerHTML = "";
    throw error;
  }
  state.comments = state.allComments.filter(
    (comment) => resolveDocId(comment.doc_id) === state.currentDocId,
  );
  renderDocumentComments();
  updateReviewBlockHighlights(ui.reader, state.comments);
  if (state.view === "board") renderBoard();
}
